export interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  indeterminate?: boolean;
  size?: 'sm' | 'md';
  disabled?: boolean;
}

function CheckIcon({ px }: { px: number }) {
  return (
    <svg width={px * 0.55} height={px * 0.55} viewBox="0 0 10 8" fill="none">
      <path d="M1 4L3.5 6.5L9 1" stroke="var(--evo-bg)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IndeterminateIcon({ px }: { px: number }) {
  return (
    <svg width={px * 0.5} height={px * 0.5} viewBox="0 0 10 2" fill="none">
      <path d="M1 1H9" stroke="var(--evo-bg)" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function Checkbox({ checked, onChange, indeterminate, size = 'sm', disabled }: CheckboxProps) {
  const px = size === 'sm' ? 16 : 18;
  const active = checked || (indeterminate ?? false);

  // Bug real, tercera vez que aparece (2026-08-02): páginas que envuelven este
  // checkbox en una fila clickeable (para que todo el ancho de la fila
  // seleccione, no solo el cuadradito) reciben el click DOS veces — una acá
  // adentro, otra por bubbling hacia el onClick de la fila — si esa fila hace
  // un toggle (no un set absoluto), las dos llamadas se cancelan entre sí y
  // el click en el checkbox no hace nada. El parche de cada página
  // (pointerEvents:'none' + wrapper) tapaba el síntoma ahí, pero cualquier
  // fila nueva que no lo copie vuelve a romperse — closest, arreglarlo acá
  // adentro para que ningún caller tenga que acordarse de nada.
  function handleClick(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    if (disabled) return;
    if (indeterminate) {
      onChange(true);
    } else {
      onChange(!checked);
    }
  }

  return (
    <div
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      tabIndex={disabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={e => { if ((e.key === ' ' || e.key === 'Enter') && !disabled) { e.preventDefault(); handleClick(e); } }}
      style={{
        width: px,
        height: px,
        flexShrink: 0,
        borderRadius: 3,
        border: `1.5px solid ${active ? 'var(--evo-cyan)' : 'var(--evo-fg-3)'}`,
        background: active ? 'var(--evo-cyan)' : 'var(--evo-bg-3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'border-color 100ms, background 100ms',
        opacity: disabled ? 0.4 : 1,
        outline: 'none',
        userSelect: 'none',
      }}
      onMouseEnter={e => {
        if (!disabled && !active) {
          (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--evo-cyan)';
        }
      }}
      onMouseLeave={e => {
        if (!disabled && !active) {
          (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--evo-fg-3)';
        }
      }}
    >
      {indeterminate ? <IndeterminateIcon px={px} /> : checked ? <CheckIcon px={px} /> : null}
    </div>
  );
}
